import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  Armchair,
  UtensilsCrossed,
  MapPin,
  Search,
  Coffee,
  Wheat,
  MessageSquareHeart,
  Heart,
  ShieldCheck,
  ChevronRight,
} from "lucide-react";
import Marquee from "../components/Marquee";
import { BRAND, MENU_ITEMS, SPACES, STORIES_NUMBERS, LOCATIONS } from "../data/content";

const NEAR_LOCATIONS = [
  { name: "Stories Café, Koramangala", meta: "450 m · Open · Closes 11:00 PM" },
  { name: "Stories Café, Indiranagar", meta: "1.2 km · Open · Closes 10:30 PM" },
  { name: "Stories Café, Jayanagar", meta: "2.1 km · Open · Closes 10:30 PM" },
];

const TRUST = [
  { icon: Wheat, t: "Millet-Based Goodness", d: "Wholesome & Delicious" },
  { icon: MessageSquareHeart, t: "Made for Conversations", d: "Where connections grow" },
  { icon: Heart, t: "Warm. Welcoming. Yours.", d: "Stories in every visit" },
  { icon: ShieldCheck, t: "Trusted by Thousands", d: "Loved across India" },
];

export default function Home() {
  const teaserMenu = MENU_ITEMS.slice(0, 4);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-28 pb-12 sm:pt-32 sm:pb-16 bg-grain overflow-hidden">
        <img
          src={BRAND.milletGraphic}
          alt=""
          aria-hidden="true"
          className="pointer-events-none select-none absolute -left-16 bottom-0 w-[28rem] opacity-25 rotate-[-12deg] hidden md:block"
        />
        <img
          src={BRAND.milletGraphic}
          alt=""
          aria-hidden="true"
          className="pointer-events-none select-none absolute -right-20 -top-10 w-[26rem] opacity-20 rotate-[160deg] hidden md:block"
        />
        <div className="absolute top-20 left-0 w-40 h-40 rounded-full bg-[#D4A373]/25 blur-3xl hidden lg:block"></div>
        <div className="absolute bottom-0 right-0 w-72 h-72 rounded-full bg-[#C76D4B]/15 blur-3xl hidden lg:block"></div>

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center w-full">
          <div className="lg:col-span-5 fade-up">
            <span className="text-xs tracking-[0.32em] uppercase text-[#C76D4B]">
              India's conversation-first millet café
            </span>
            <h1 className="font-serif-display text-6xl sm:text-7xl lg:text-[5.5rem] leading-[0.95] mt-6 text-[#2A1E17]">
              Your table.
              <br />
              Your time.
              <br />
              <em className="italic text-[#C76D4B]">Your story.</em>
            </h1>
            <div className="w-24 h-[3px] bg-[#C76D4B] rounded-full mt-7"></div>
            <p className="mt-8 text-base sm:text-lg text-[#4A3320] max-w-md leading-relaxed">
              Find the nearest Stories café, reserve a cozy corner, or order your café favourites.
            </p>

            <div className="mt-10 inline-flex items-start gap-4 bg-[#F3EBE1] rounded-2xl px-5 py-4 max-w-md border border-[#E8D8C8]">
              <div className="h-10 w-10 rounded-xl bg-white grid place-items-center border border-[#E8D8C8]">
                <Coffee size={18} className="text-[#C76D4B]" />
              </div>
              <p className="text-sm text-[#4A3320] leading-relaxed">
                A cozy millet café for dates, meetings, reads and meaningful conversations.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 relative fade-up" style={{ animationDelay: ".15s" }}>
            <div className="relative overflow-hidden rounded-[2rem] border border-[#E8D8C8] shadow-2xl shadow-[#4A3320]/15">
              <img
                src={BRAND.heroAmbience}
                alt="Stories — The Millet Café interior"
                style={{ objectPosition: "right center" }}
                className="w-full h-[440px] sm:h-[520px] lg:h-[560px] object-cover img-zoom"
              />
              <div className="absolute top-5 left-5 flex flex-wrap gap-2">
                <span className="bg-[#FAF7F2]/95 backdrop-blur text-[10px] tracking-[0.22em] uppercase text-[#C76D4B] px-3 py-1.5 rounded-full">
                  Today's Special
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK ACTIONS + FIND A CAFE WIDGET */}
      <section className="px-5 sm:px-8 pb-20 sm:pb-24">
        <div className="max-w-7xl mx-auto bg-[#F3EBE1] rounded-[2.5rem] p-5 sm:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Action cards */}
          <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <ActionCard
              testid="action-reserve"
              to="/contact"
              tone="terracotta"
              Icon={Armchair}
              title="Reserve a Corner"
              desc="Book your cozy corner for dates, meetings or quiet me-time."
              imageSrc={BRAND.storefront}
              btnLabel="Reserve Now"
            />
            <ActionCard
              testid="action-order"
              to="/menu"
              tone="terracotta"
              Icon={UtensilsCrossed}
              title="Order Café Food"
              desc="Order your favourite millet-based meals and treats."
              imageSrc={BRAND.foodSpread}
              btnLabel="Order Now"
            />
            <ActionCard
              testid="action-find"
              to="/locations"
              tone="sage"
              Icon={MapPin}
              title="Find Stories Near You"
              desc="Locate the nearest Stories café and plan your visit."
              imageSrc={BRAND.cozyNook}
              btnLabel="Find Near Me"
            />
          </div>

          {/* Find a Stories near you widget */}
          <div className="lg:col-span-6 bg-white rounded-3xl border border-[#E8D8C8] p-5 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="flex flex-col">
              <h3 className="font-serif-display text-2xl leading-tight">Find a Stories Café Near You</h3>
              <label className="relative mt-5">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7A5C45]" />
                <input
                  data-testid="hero-location-search"
                  className="w-full bg-[#FAF7F2] border border-[#E8D8C8] rounded-full pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#C76D4B]"
                  placeholder="Enter your location"
                />
              </label>
              <ul className="mt-5 space-y-3 flex-1">
                {NEAR_LOCATIONS.map((l, i) => (
                  <li key={l.name}>
                    <Link
                      to="/locations"
                      data-testid={`near-location-${i}`}
                      className="group flex items-center gap-3 p-3 rounded-2xl hover:bg-[#F3EBE1] transition"
                    >
                      <span className="h-9 w-9 rounded-full bg-[#C76D4B]/15 text-[#C76D4B] grid place-items-center flex-none">
                        <MapPin size={16} />
                      </span>
                      <span className="flex-1">
                        <span className="block font-medium text-sm text-[#2A1E17]">{l.name}</span>
                        <span className="block text-[11px] text-[#7A5C45] mt-0.5">
                          {l.meta.split("·").map((seg, idx) =>
                            seg.trim() === "Open" ? (
                              <span key={idx} className="text-[#7B8B6F] font-medium">
                                {" Open "}·{" "}
                              </span>
                            ) : (
                              <span key={idx}>{seg}{idx < 2 ? " · " : ""}</span>
                            )
                          )}
                        </span>
                      </span>
                      <ChevronRight size={16} className="text-[#7A5C45] group-hover:text-[#C76D4B] transition" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative overflow-hidden rounded-2xl border border-[#E8D8C8] min-h-[300px]">
              <iframe
                title="Stories cafés near you"
                src="https://maps.google.com/maps?q=Indiranagar%2C%20Bengaluru&t=&z=13&ie=UTF8&iwloc=&output=embed"
                className="absolute inset-0 w-full h-full grayscale-[20%] saturate-[0.85]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="absolute inset-0 bg-[#FAF7F2]/15 pointer-events-none" />
              <div className="absolute top-4 left-4 bg-[#FAF7F2] rounded-full px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase text-[#C76D4B] border border-[#E8D8C8]">
                Live map
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="px-5 sm:px-8 pb-20">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-5 gap-6 items-center border-y border-[#E8D8C8] py-8">
          {TRUST.map((t, i) => (
            <div key={t.t} data-testid={`trust-${i}`} className="flex items-start gap-3">
              <span className="h-10 w-10 rounded-full bg-[#F3EBE1] grid place-items-center flex-none">
                <t.icon size={18} className="text-[#C76D4B]" />
              </span>
              <span>
                <span className="block text-sm font-semibold text-[#2A1E17] leading-tight">{t.t}</span>
                <span className="block text-xs text-[#7A5C45] mt-1">{t.d}</span>
              </span>
            </div>
          ))}
          <div className="col-span-2 md:col-span-1 flex md:justify-end items-center gap-3">
            <span className="text-xs tracking-[0.22em] uppercase text-[#7A5C45]">A part of</span>
            <span
              className="font-serif-display text-3xl text-[#D4A373] leading-none"
              data-testid="ebg-badge"
            >
              EBG
              <span className="block text-[10px] tracking-[0.32em] text-[#7A5C45] mt-1">GROUP</span>
            </span>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee items={["Real Food", "Real Conversations", "Real Impact", "Millet First", "Made For Dwell Time"]} />

      {/* THE HUMAN GAP */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 relative">
            <div className="overflow-hidden rounded-[2rem]">
              <img
                src={BRAND.conversationImage}
                alt="Friends in conversation at Stories café"
                className="w-full h-[560px] object-cover img-zoom"
              />
            </div>
            <span className="absolute -top-4 -left-4 font-script text-[#C76D4B] text-3xl rotate-[-6deg]">
              the human gap →
            </span>
            <div className="absolute -bottom-6 right-6 bg-[#FAF7F2] border border-[#E8D8C8] rounded-2xl px-5 py-4 shadow-xl shadow-[#4A3320]/15 max-w-[230px] hidden sm:block">
              <div className="font-serif-display italic text-lg leading-tight text-[#2A1E17]">
                "Good conversations need time. We've got you covered."
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <span className="text-xs tracking-[0.32em] uppercase text-[#C76D4B]">The Human Gap</span>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.04]">
              Real conversations <br /> need <em className="italic text-[#C76D4B]">better places.</em>
            </h2>
            <p className="mt-6 text-[#7A5C45] max-w-xl leading-relaxed">
              We're surrounded by coffee shops, but starved for spaces that invite us to stay.
              Stories is built for the long sit — comfortable seating, food worth sharing and a
              soundtrack that lets you actually hear each other.
            </p>
            <div className="grid grid-cols-2 gap-6 mt-10">
              {[
                { t: "Comfortable ambience" },
                { t: "Food worth sharing" },
                { t: "Long-stay seating" },
                { t: "Designed for real moments" },
              ].map((f, i) => (
                <div key={i} className="flex items-start gap-3" data-testid={`gap-feature-${i}`}>
                  <span className="h-7 w-7 rounded-full bg-[#C76D4B] text-white grid place-items-center text-xs font-medium">
                    {i + 1}
                  </span>
                  <span className="text-sm font-medium text-[#4A3320]">{f.t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MENU TEASER */}
      <section className="bg-[#F3EBE1] py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
            <div>
              <span className="text-xs tracking-[0.32em] uppercase text-[#C76D4B]">Menu</span>
              <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.04]">
                A millet menu <br /> people actually <em className="italic">crave.</em>
              </h2>
            </div>
            <Link to="/menu" data-testid="home-view-menu-btn" className="btn-secondary">
              View Full Menu <ArrowUpRight size={18} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {teaserMenu.map((m, i) => (
              <div key={m.name} className="card-lift bg-white rounded-3xl overflow-hidden border border-[#E8D8C8]" data-testid={`menu-teaser-${i}`}>
                <div className="overflow-hidden">
                  <img src={m.img} alt={m.name} className="w-full h-56 object-cover img-zoom" />
                </div>
                <div className="p-6">
                  <span className="text-[10px] tracking-[0.22em] uppercase text-[#C76D4B]">{m.tag}</span>
                  <h3 className="font-serif-display text-xl mt-2 leading-tight">{m.name}</h3>
                  <p className="text-xs text-[#7A5C45] mt-2 line-clamp-2">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONVERSATION COVER CONCEPT */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="text-xs tracking-[0.32em] uppercase text-[#C76D4B]">Stay. Talk. Eat. Redeem.</span>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.04]">
              The <em className="italic text-[#C76D4B]">Conversation Cover.</em>
            </h2>
            <p className="mt-6 text-[#7A5C45] max-w-xl leading-relaxed">
              A smarter café cover model for longer, better evenings. Reserve a corner, get a
              cover that's redeemable against food, and stay as long as the conversation deserves.
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { t: "Reserved time", d: "Your corner. Your hour." },
                { t: "Redeemable", d: "Cover applies to food." },
                { t: "Built for dwell", d: "Stay. No rush." },
              ].map((b, i) => (
                <div key={i} className="bg-[#F3EBE1] rounded-2xl p-5">
                  <div className="font-serif-display text-xl">{b.t}</div>
                  <div className="text-xs text-[#7A5C45] mt-2">{b.d}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative bg-[#2A1E17] text-[#FAF7F2] rounded-[2rem] p-8 sm:p-10 overflow-hidden">
              <img
                src={BRAND.milletGraphic}
                alt=""
                aria-hidden="true"
                className="absolute -right-12 -bottom-10 w-72 opacity-30 rotate-12 pointer-events-none select-none"
              />
              <div className="relative">
                <div className="text-xs tracking-[0.22em] uppercase text-[#D4A373]">Indicative Cover</div>
                <div className="font-serif-display text-6xl mt-4">₹300</div>
                <div className="text-sm text-[#FAF7F2]/70 mt-1">per guest, per hour · redeemable</div>
                <p className="mt-6 text-sm text-[#FAF7F2]/75">
                  Choose your plan when you book — Solo Pause, Couple Corner, Group Table or
                  Creative Session.
                </p>
                <Link to="/contact" data-testid="cover-reserve-btn" className="btn-primary mt-8 bg-[#C76D4B] hover:bg-[#A55639]">
                  Reserve with Cover <ArrowUpRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SPACES PREVIEW */}
      <section className="py-24 sm:py-32 bg-[#F3EBE1]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div>
              <span className="text-xs tracking-[0.32em] uppercase text-[#C76D4B]">Spaces</span>
              <h2 className="font-serif-display text-4xl sm:text-5xl mt-4">
                Every corner has a <em className="italic">story.</em>
              </h2>
            </div>
            <Link to="/about" className="btn-secondary" data-testid="spaces-explore-btn">
              The Stories Experience <ArrowUpRight size={18} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SPACES.map((s, i) => (
              <div key={s.name} data-testid={`space-${i}`} className="card-lift rounded-3xl overflow-hidden bg-white border border-[#E8D8C8]">
                <div className="overflow-hidden">
                  <img src={s.img} alt={s.name} className="w-full h-48 object-cover img-zoom" />
                </div>
                <div className="p-6">
                  <h3 className="font-serif-display text-xl">{s.name}</h3>
                  <p className="text-sm text-[#7A5C45] mt-2 line-clamp-2">{s.desc}</p>
                  <div className="text-[11px] tracking-[0.18em] uppercase text-[#C76D4B] mt-4">{s.best}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NUMBERS */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[#C76D4B]/5 blur-3xl pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
            <div>
              <span className="text-xs tracking-[0.32em] uppercase text-[#C76D4B]">By the numbers</span>
              <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.04]">
                A young brand, <br />a <em className="italic text-[#C76D4B]">loud</em> impact.
              </h2>
            </div>
            <p className="text-[#7A5C45] max-w-md text-base leading-relaxed">
              Numbers we're proud of — but the real story is in the conversations our cafés
              spark every single day.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STORIES_NUMBERS.map((n, i) => {
              const Icon = [Wheat, Coffee, MapPin, Heart][i] || Wheat;
              const tones = [
                { bg: "bg-[#C76D4B]", soft: "bg-[#C76D4B]/12", text: "text-[#C76D4B]" },
                { bg: "bg-[#D4A373]", soft: "bg-[#D4A373]/20", text: "text-[#A55639]" },
                { bg: "bg-[#7B8B6F]", soft: "bg-[#7B8B6F]/15", text: "text-[#7B8B6F]" },
                { bg: "bg-[#2A1E17]", soft: "bg-[#2A1E17]/10", text: "text-[#2A1E17]" },
              ];
              const tone = tones[i % tones.length];
              return (
                <div
                  key={i}
                  data-testid={`number-${i}`}
                  className={`group card-lift relative rounded-[2rem] p-7 sm:p-8 bg-[#FAF7F2] border border-[#E8D8C8] overflow-hidden ${
                    i % 2 === 1 ? "lg:mt-10" : ""
                  }`}
                >
                  <div className={`absolute -right-10 -top-10 w-40 h-40 rounded-full ${tone.soft} blur-2xl transition-opacity duration-500 opacity-70 group-hover:opacity-100`}></div>

                  <div className="relative flex items-start justify-between">
                    <span className={`h-11 w-11 rounded-full ${tone.bg} text-white grid place-items-center`}>
                      <Icon size={18} />
                    </span>
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[#7A5C45]">
                      0{i + 1}
                    </span>
                  </div>

                  <div className={`relative font-serif-display text-7xl sm:text-[5.5rem] leading-none mt-8 ${tone.text}`}>
                    {n.value}
                  </div>

                  <div className="relative mt-6 pt-5 border-t border-dashed border-[#E8D8C8]">
                    <div className="text-sm font-semibold text-[#2A1E17] leading-tight">
                      {n.label}
                    </div>
                    <div className="font-script text-2xl text-[#C76D4B] mt-2 leading-none">
                      {["pioneers", "crave-worthy", "& counting", "real, honest"][i]}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-14 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-xs tracking-[0.22em] uppercase text-[#7A5C45]">
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[#C76D4B]"></span> Farmer-direct sourcing
            </span>
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[#7B8B6F]"></span> Slow-cooked
            </span>
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[#D4A373]"></span> Made fresh daily
            </span>
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[#2A1E17]"></span> Zero shortcuts
            </span>
          </div>
        </div>
      </section>

      {/* PRE-FOOTER INVITATION CARD */}
      <section className="px-5 sm:px-8 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto relative">
          <div className="relative bg-[#F3EBE1] rounded-[2.5rem] overflow-hidden border border-[#E8D8C8]">
            <img
              src={BRAND.milletGraphic}
              alt=""
              aria-hidden="true"
              className="pointer-events-none select-none absolute -left-20 -top-10 w-[26rem] opacity-30 rotate-[-15deg] hidden md:block"
            />
            <img
              src={BRAND.milletGraphic}
              alt=""
              aria-hidden="true"
              className="pointer-events-none select-none absolute -right-20 -bottom-16 w-[28rem] opacity-25 rotate-[160deg] hidden md:block"
            />
            <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-[#C76D4B]/15 blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-44 h-44 rounded-full bg-[#D4A373]/20 blur-3xl"></div>

            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center px-7 sm:px-12 lg:px-16 py-14 sm:py-20">
              <div className="lg:col-span-7">
                <span className="text-xs tracking-[0.32em] uppercase text-[#C76D4B]">
                  An open invitation
                </span>
                <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05] text-[#2A1E17]">
                  At Stories, we don't just <br /> serve food.{" "}
                  <em className="italic text-[#C76D4B]">
                    We serve <br />moments that matter.
                  </em>
                </h2>
                <p className="font-script text-3xl text-[#C76D4B] mt-6 leading-none">
                  — your corner is waiting.
                </p>
                <div className="mt-9 flex flex-wrap gap-4">
                  <Link to="/locations" className="btn-primary" data-testid="cta-find-cafe">
                    <MapPin size={18} /> Find a Café
                  </Link>
                  <Link to="/menu" className="btn-secondary" data-testid="cta-explore-menu">
                    Explore the Menu <ArrowUpRight size={18} />
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-5 relative flex justify-center lg:justify-end">
                <div className="relative">
                  <div className="absolute inset-0 -m-4 rounded-full bg-[#FAF7F2] blur-2xl"></div>
                  <div className="relative h-56 w-56 sm:h-64 sm:w-64 rounded-full bg-white border-2 border-dashed border-[#C76D4B]/40 flex items-center justify-center p-6">
                    <img
                      src={BRAND.logoBrown}
                      alt="Stories — The Millet Café"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <span className="absolute -bottom-2 -right-2 sm:-right-6 bg-[#2A1E17] text-[#FAF7F2] text-[10px] tracking-[0.32em] uppercase px-4 py-2 rounded-full rotate-[8deg]">
                    Est · India
                  </span>
                </div>
              </div>
            </div>

            <div className="relative border-t border-dashed border-[#C76D4B]/30 px-7 sm:px-12 lg:px-16 py-5 flex flex-wrap items-center justify-between gap-3">
              <span className="text-xs tracking-[0.22em] uppercase text-[#7A5C45]">
                Open Mon — Sun · 8:00 AM — 11:00 PM
              </span>
              <span className="font-script text-2xl text-[#C76D4B] leading-none">
                Real Food · Real Conversations · Real Impact
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ActionCard({ to, testid, tone, Icon, title, desc, imageSrc, btnLabel }) {
  const toneStyles =
    tone === "sage"
      ? { circle: "bg-[#7B8B6F]/15 text-[#7B8B6F]", btn: "bg-[#7B8B6F] hover:bg-[#5f6f57]" }
      : { circle: "bg-[#C76D4B]/15 text-[#C76D4B]", btn: "bg-[#C76D4B] hover:bg-[#A55639]" };
  return (
    <div
      data-testid={testid}
      className="card-lift bg-white rounded-3xl border border-[#E8D8C8] p-5 flex flex-col"
    >
      <div className={`h-12 w-12 rounded-full grid place-items-center ${toneStyles.circle}`}>
        <Icon size={20} />
      </div>
      <h3 className="font-serif-display text-2xl mt-5 leading-tight">{title}</h3>
      <p className="text-xs text-[#7A5C45] mt-3 leading-relaxed">{desc}</p>
      <div className="mt-5 overflow-hidden rounded-2xl">
        <img src={imageSrc} alt={title} className="w-full h-32 object-cover img-zoom" />
      </div>
      <Link
        to={to}
        className={`mt-5 inline-flex items-center justify-center gap-2 text-white rounded-full py-3 text-sm font-medium transition ${toneStyles.btn}`}
      >
        {btnLabel} <ArrowRight size={15} />
      </Link>
    </div>
  );
}
